import { usePermission } from '../../../../auth/AuthContext';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import type { AwardNomineeDetail } from '../praiseTypes';

const points = (value: number) => value.toFixed(2);

/**
 * A nominee's Scores tab — **Your score** (Evaluation only: the caller's own sheet, its own Edit under
 * `award-evaluations.score`) over **Results** (every evaluator's total and the average, read-only: a
 * derived view of the sheets, one of the cards that legitimately has no Edit).
 *
 * Each evaluator scores separately; the result is the AVERAGE of their totals (product call, 31 Oct 2026).
 */
export function NomineeScoresPanel({
  surfaceKey,
  record,
  closedReason,
  onScore,
}: {
  surfaceKey: 'nomination' | 'evaluation';
  record: AwardNomineeDetail;
  /** Why no score can be saved now — STATE, so the Edit disables and says it. */
  closedReason: string | null;
  onScore: () => void;
}) {
  const mine = record.scoreSheets.find((s) => s.isMine) ?? null;
  // HIDDEN without the key — "you have not scored" addressed to a reader who cannot score is a false prompt.
  const canScore = usePermission('award-evaluations.score');
  const pointsOf = (criterionId: string) => mine?.lines.find((l) => l.awardCriterionId === criterionId)?.points;

  return (
    <>
      {surfaceKey === 'evaluation' && canScore ? (
        <ProfileCard
          id="evaluation-nominee-profile-my-score"
          title="Your score"
          onEdit={onScore}
          editPermission="award-evaluations.score"
          editDisabledReason={record.criteria.length === 0 ? 'This award has no criteria to score against.' : closedReason}
          // Not scored yet says so AND offers the way out — the card's own Edit.
          empty={
            mine === null
              ? {
                  title: 'You have not scored this nominee yet',
                  description: `Score each of the ${record.criteria.length} criteria from 0 to its maximum — ${record.maxTotal} points in all.`,
                }
              : undefined
          }
        >
          {mine === null ? null : (
            <PreviewTable
              variant="table-display"
              rows={[
                ...record.criteria.map((c) => {
                  const given = pointsOf(c.id);
                  return {
                    label: `${c.name} (max ${c.maxPoints})`,
                    value: given != null ? points(given) : null,
                    mono: given != null,
                  };
                }),
                { label: 'Total', value: `${points(mine.total)} of ${record.maxTotal}`, mono: true },
                {
                  label: 'Saved',
                  value: (
                    <time dateTime={toIso(mine.scoredAt)} className="font-mono">
                      {formatDateTime(mine.scoredAt)}
                    </time>
                  ),
                },
              ]}
            />
          )}
        </ProfileCard>
      ) : null}

      <ProfileCard id={`${surfaceKey}-nominee-profile-results`} title="Results" count={record.scoreSheets.length}>
        <PreviewTable
          variant="table-display"
          rows={[
            {
              label: 'Average score',
              value: record.averageScore != null ? `${points(record.averageScore)} of ${record.maxTotal}` : 'Not scored yet',
              mono: record.averageScore != null,
            },
            { label: 'Evaluators', value: String(record.scoreSheets.length), mono: true },
            ...record.scoreSheets.map((s) => ({
              label: `${s.evaluatorName ?? 'An account no longer listed'}${s.isMine ? ' (you)' : ''}`,
              value: `${points(s.total)} of ${record.maxTotal}`,
              mono: true,
            })),
          ]}
        />
      </ProfileCard>
    </>
  );
}
